import { useContext } from 'react';
import { graphqlFetch } from '@shared/api';
import {
	feedQueryDocument,
	issuesQueryDocument,
	sectionsQueryDocument,
} from './home.document';
import {
	QueryClient,
	QueryKey,
	useQuery,
	useQueryClient,
} from '@tanstack/react-query';

import { AppContext } from '@/app.context';
import { Issue, USER_ROLE, Section } from '@/constants';

import config from '@/config';
import type { GetFeedResponse } from '@pages/games';
import { getQueryCacheOptions } from '@shared/query-client/query-client';
import { Logger } from '@shared/lib/logger';

const logger = new Logger('starmagazine-home');

// Exported so `home.tsx` (rendering) and `home.loader.ts` (route-loader
// prefetch) share one source of truth instead of the loader duplicating
// this magic number.
export const ISSUE_ID_FEED = 1633;

export function useLatestIssue(): GetFeedResponse {
	const queryKey = ['issue', 'latest'];
	const queryClient = useQueryClient();
	const { onlineStatus: isOnline } = useContext(AppContext);
	const { parseGraphQLClientError } = useContext(AppContext);

	const isAdmin = localStorage.getItem(USER_ROLE) === 'admin';
	const cacheOptions = getQueryCacheOptions({
		isOnline,
		isAdmin,
		ttlMinutes: config.app?.cache?.ttl?.getAllIssues,
	});

	const { data, isPending, error } = useQuery<{ issues: Array<Issue> }>({
		queryKey,
		networkMode: 'always',
		queryFn: async () =>
			getLatestIssue({
				queryClient,
				queryKey,
				isOnline,
			}),
		staleTime: cacheOptions.staleTime,
		gcTime: cacheOptions.gcTime,
		retry: isOnline ? 3 : 0,
	});

	let issue = null;
	if (data?.issues.length) {
		issue = data.issues[0];
	}

	return {
		issue,
		isLoading: isPending,
		error: error ? parseGraphQLClientError(error) : null,
	};
}

async function getLatestIssue({
	queryKey,
	queryClient,
	isOnline,
}: {
	queryClient: QueryClient;
	queryKey: QueryKey;
	isOnline: boolean;
}): Promise<{ issues: Array<Issue> }> {
	const cached: { issues: Array<Issue> } | undefined =
		queryClient.getQueryData(queryKey);

	// The app is offline
	if (!isOnline) {
		if (!cached) return { issues: [] };
		return cached;
	}

	try {
		// Cast: generated type's nullability is honest where the shared
		// Issue type claims required fields; reconciling that is topic 5's
		// separate "derive Issue/Article/Style from generated types" step.
		return (await graphqlFetch(
			issuesQueryDocument,
			{
				sortKey: 'RELEASE_DATE',
				limit: 1,
				reverse: true,
				offset: 0,
			},
			{ withAuth: false },
		)) as unknown as Promise<{ issues: Array<Issue> }>;
	} catch (e) {
		logger.error(e);
		if (cached) return cached;
	}

	return { issues: [] };
}

export function useIssues(): GetIssuesResponse {
	const isAdmin = localStorage.getItem(USER_ROLE) === 'admin';
	const { parseGraphQLClientError, onlineStatus: isOnline } =
		useContext(AppContext);
	const cacheOptions = getQueryCacheOptions({
		isOnline,
		isAdmin,
		ttlMinutes: config.app?.cache?.ttl?.getAllIssues,
	});
	const { data, isPending, error } = useQuery<{ issues: Array<Issue> }>({
		queryKey: ['issues'],
		queryFn: async () =>
			// Cast: generated type's nullability is honest where the shared
			// Issue type claims required fields; reconciling that is topic 5's
			// separate "derive Issue/Article/Style from generated types" step.
			graphqlFetch(issuesQueryDocument, {
				sortKey: 'RELEASE_DATE',
				limit: 10,
				reverse: true,
				offset: 1,
			}) as unknown as Promise<{ issues: Array<Issue> }>,
		staleTime: cacheOptions.staleTime,
		gcTime: cacheOptions.gcTime,
	});

	return {
		issues: data?.issues || [],
		isLoading: isPending,
		error: error ? parseGraphQLClientError(error) : null,
	};
}

export interface GetIssuesResponse {
	issues: Array<Issue>;
	isLoading: boolean;
	error?: Error | null;
}

export function useSections(): GetSectionsResponse {
	const queryKey = ['sections'];
	const queryClient = useQueryClient();
	const { onlineStatus: isOnline } = useContext(AppContext);

	const { parseGraphQLClientError } = useContext(AppContext);

	const isAdmin = localStorage.getItem(USER_ROLE) === 'admin';
	const cacheOptions = getQueryCacheOptions({
		isOnline,
		isAdmin,
		ttlMinutes: config.app?.cache?.ttl?.getAllIssues,
	});

	const { data, isPending, error } = useQuery<{ sections: Array<Section> }>({
		queryKey,
		networkMode: 'always',
		queryFn: async () => getSections({ queryClient, queryKey, isOnline }),
		staleTime: cacheOptions.staleTime,
		gcTime: cacheOptions.gcTime,
		retry: isOnline ? 3 : 0,
	});

	return {
		sections: data?.sections || [],
		isLoading: isPending,
		error: error ? parseGraphQLClientError(error) : null,
	};
}

export async function getSections({
	queryClient,
	queryKey,
	isOnline,
}: {
	queryClient: QueryClient;
	queryKey: QueryKey;
	isOnline: boolean;
}): Promise<{ sections: Array<Section> }> {
	const cached: { sections: Array<Section> } | undefined =
		queryClient.getQueryData(queryKey);

	// The app is offline
	if (!isOnline) {
		if (!cached) return { sections: [] };
		return cached;
	}

	try {
		// Cast: generated type's nullability is honest where the shared
		// Section/Issue types claim required fields; reconciling that is
		// topic 5's separate "derive Issue/Article/Style from generated
		// types" step.
		return (await graphqlFetch(
			sectionsQueryDocument,
			{
				sortKey: 'RELEASE_DATE',
				limit: 10,
				reverse: true,
			},
			{ withAuth: false },
		)) as unknown as Promise<{ sections: Array<Section> }>;
	} catch (e) {
		logger.error(e);
		if (cached) return cached;
	}

	return { sections: [] };
}

interface GetSectionsResponse {
	sections: Array<Section>;
	isLoading: boolean;
	error?: Error | null;
}

export function useFeed({ id, limit }: FeedArgs): GetFeedResponse {
	const queryKey = ['issue', id, { feed: true }];
	const queryClient = useQueryClient();
	const { onlineStatus: isOnline } = useContext(AppContext);
	const { parseGraphQLClientError } = useContext(AppContext);

	const isAdmin = localStorage.getItem(USER_ROLE) === 'admin';
	const cacheOptions = getQueryCacheOptions({
		isOnline,
		isAdmin,
		ttlMinutes: 1,
	});

	const { data, isPending, error } = useQuery<{ issue: Issue | null }>({
		queryKey,
		networkMode: 'always',
		queryFn: async () =>
			getFeed({
				queryKey,
				queryClient,
				id,
				limit,
				isOnline,
			}),
		staleTime: cacheOptions.staleTime,
		gcTime: cacheOptions.gcTime,
		retry: isOnline ? 3 : 0,
	});

	return {
		issue: data?.issue || null,
		isLoading: isPending,
		error: error ? parseGraphQLClientError(error) : null,
	};
}

interface FeedArgs {
	id: string;
	limit: number;
}

export async function getFeed({
	queryKey,
	queryClient,
	id,
	limit,
	isOnline,
}: {
	queryClient: QueryClient;
	queryKey: QueryKey;
	id: string | number;
	limit: number;
	isOnline: boolean;
}): Promise<{ issue: Issue | null }> {
	const cached: { issue: Issue | null } | undefined =
		queryClient.getQueryData(queryKey);

	// The app is offline
	if (!isOnline) {
		if (!cached) return { issue: null };
		return cached;
	}

	try {
		// Cast: generated type's nullability is honest where the shared
		// Issue/Article types claim required fields; reconciling that is
		// topic 5's separate "derive Issue/Article/Style from generated
		// types" step.
		return (await graphqlFetch(
			feedQueryDocument,
			{
				id,
				sortKey: 'INDEX',
				reverse: false,
				limit,
			},
			{ withAuth: false },
		)) as unknown as Promise<{ issue: Issue | null }>;
	} catch (e) {
		logger.error(e);
		if (cached) return cached;
	}
	return { issue: null };
}
